import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import type { Config, Driver } from 'driver.js';
import { useAuthContext } from '../contexts/AuthContext';
import { useProductTourContext } from '../contexts/ProductTourContext';
import api from '../lib/api';
import { CURRENT_PRODUCT_TOUR_VERSION } from '../constants/productTour';
import { buildProductTourSteps } from '../productTour/buildProductTourSteps';
import { isOrgAdmin } from '../lib/orgRoles';

/** Close/Done/overlay dismiss all POST completion so we do not nag users who skip once. */
async function postTourComplete(): Promise<void> {
  try {
    await api.post('/auth/me/product-tour/complete');
  } catch {
    // Non-blocking; tour still ends in the UI.
  }
}

export function ProductTour() {
  const { user } = useAuthContext();
  const { manualTourRequest } = useProductTourContext();
  const location = useLocation();
  const driverRef = useRef<Driver | null>(null);
  const autoStartedRef = useRef(false);
  const runTourRef = useRef<(isAuto: boolean) => void>(() => {});

  const [authTourVersion, setAuthTourVersion] = useState<number | null>(null);
  const [questionnaireCompleted, setQuestionnaireCompleted] = useState<boolean | null>(null);
  const [prefsLoaded, setPrefsLoaded] = useState(false);

  const isSessionRoute = /^\/practice\/(?!new$)[^/]+/.test(location.pathname);

  useEffect(() => {
    let cancelled = false;

    void Promise.all([
      api
        .get<{ productTourVersionCompleted: number }>('/auth/me')
        .then((res) => res.data.productTourVersionCompleted)
        .catch(() => CURRENT_PRODUCT_TOUR_VERSION),
      api
        .get<{ questionnaireCompleted?: boolean }>('/orgs/me')
        .then((res) => res.data.questionnaireCompleted ?? true)
        .catch(() => true),
    ]).then(([version, qc]) => {
      if (cancelled) return;
      setAuthTourVersion(version);
      setQuestionnaireCompleted(qc);
      setPrefsLoaded(true);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const shouldOfferAutoTour =
    prefsLoaded &&
    authTourVersion !== null &&
    authTourVersion < CURRENT_PRODUCT_TOUR_VERSION &&
    questionnaireCompleted === true &&
    !isSessionRoute;

  const runTour = useCallback(
    (isAuto: boolean) => {
      if (!user) return;
      if (isAuto && questionnaireCompleted === false) return;

      const isAdmin = isOrgAdmin(user.orgRole);
      const steps = buildProductTourSteps({
        isAdmin,
        isPlatformAdmin: Boolean(user.isPlatformAdmin),
      });

      const onDestroyed: NonNullable<Config['onDestroyed']> = () => {
        void postTourComplete();
        driverRef.current = null;
        setAuthTourVersion(CURRENT_PRODUCT_TOUR_VERSION);
      };

      driverRef.current?.destroy();

      const d = driver({
        showProgress: true,
        smoothScroll: true,
        allowClose: true,
        overlayOpacity: 0.55,
        stagePadding: 8,
        popoverClass: 'akoreps-driver-popover',
        steps,
        onDestroyed,
      });

      driverRef.current = d;
      d.drive();
    },
    [user, questionnaireCompleted],
  );

  runTourRef.current = runTour;

  useEffect(() => {
    if (!prefsLoaded || !shouldOfferAutoTour || autoStartedRef.current) return;
    autoStartedRef.current = true;
    const t = window.setTimeout(() => runTour(true), 400);
    return () => window.clearTimeout(t);
  }, [prefsLoaded, shouldOfferAutoTour, runTour]);

  useEffect(() => {
    if (manualTourRequest < 1) return;
    const t = window.setTimeout(() => runTourRef.current(false), 0);
    return () => window.clearTimeout(t);
  }, [manualTourRequest]);

  return null;
}
